import { supabase } from "./supabase";

export type CaiOpportunityRequest = {
  accountId: string;
  accountType?: string | null;
  runId?: string | null;
  recommendationId?: string | null;
  recommendationKey?: string | null;
  recommendationSourceKey?: string | null;
  recommendationType?: string | null;
  entityArea?: string | null;
  targetEntity?: string | null;
  recommendedOutput?: string | null;
  targetAsset?: string | null;
  targetAudience?: string | null;
  confidence?: string | number | null;
  evidencePattern?: string | null;
  expectedResult?: string | null;
  nextStep?: string | null;
  actionBriefId?: string | null;
  idempotencyKey?: string;
  productArea: "local" | "competitive";
  assetFocused?: boolean;
  recommendationTitle?: string;
  opportunityCategory?: "asset" | "entity" | "competitive" | null;
};

export type CaiDurableJobSubmission = CaiOpportunityRequest & {
  retryRequested?: boolean;
};

export type ResumeExistingCaiOpportunityJobRequest = {
  resumeExistingJobId: string;
  accountId: string;
  runId: string;
  recommendationId: string;
  recommendationSourceKey: string;
  productArea: "local" | "competitive";
};

async function accessToken(): Promise<string> {
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token || "";
  } catch {
    return "";
  }
}

async function safeJson(response: Response): Promise<Record<string, any>> {
  try {
    const value = await response.json();
    return value && typeof value === "object" ? value : {};
  } catch {
    return {};
  }
}

export async function submitCaiOpportunityJob(input: CaiDurableJobSubmission): Promise<Record<string, any>> {
  const token = await accessToken();
  if (!token) return { status: "needs_configuration", code: "cai_auth_required" };
  try {
    const response = await fetch("/api/cai-opportunity-job", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ ...input, retryRequested: input.retryRequested === true }),
    });
    const body = await safeJson(response);
    if (body.code === "cai_preview_mutation_not_allowed") return { status: "preview_read_only", code: body.code };
    return body.status || body.code ? body : { status: "needs_configuration", code: "cai_job_submission_unavailable" };
  } catch {
    return { status: "needs_configuration", code: "cai_job_submission_unavailable" };
  }
}

const SAFE_RECOVERY_STATUSES = new Set(["pending", "running", "completed", "failed", "needs_review", "quota_exhausted"]);
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function sanitizedRecoveryResult(body: Record<string, any>, httpStatus: number): Record<string, any> {
  const status = SAFE_RECOVERY_STATUSES.has(body.status) ? body.status : "error";
  const code = typeof body.code === "string" && /^[a-z0-9_]{1,100}$/.test(body.code)
    ? body.code
    : httpStatus >= 200 && httpStatus < 300 ? "cai_recovery_response_unavailable" : "cai_recovery_request_rejected";
  const sourceJob = body.job && typeof body.job === "object" ? body.job : {};
  const jobId = typeof sourceJob.id === "string" && UUID_PATTERN.test(sourceJob.id) ? sourceJob.id : null;
  const dispatchStatus = ["not_attempted", "accepted", "failed", "retryable", "unknown"].includes(body.dispatch?.status)
    ? body.dispatch.status
    : null;
  return {
    status,
    code,
    httpStatus,
    recovery: body.recovery === "existing_job_only" ? "existing_job_only" : null,
    job: jobId ? {
      id: jobId,
      accountId: typeof sourceJob.accountId === "string" && UUID_PATTERN.test(sourceJob.accountId) ? sourceJob.accountId : null,
      runId: typeof sourceJob.runId === "string" && UUID_PATTERN.test(sourceJob.runId) ? sourceJob.runId : null,
      recommendationId: typeof sourceJob.recommendationId === "string" && UUID_PATTERN.test(sourceJob.recommendationId) ? sourceJob.recommendationId : null,
      status: SAFE_RECOVERY_STATUSES.has(sourceJob.status) ? sourceJob.status : null,
      dispatchStatus: ["queued", "accepted", "failed", "retryable", "unknown"].includes(sourceJob.dispatchStatus) ? sourceJob.dispatchStatus : null,
      usageCounted: sourceJob.usageCounted === true,
    } : null,
    dispatch: body.dispatch && typeof body.dispatch === "object" ? {
      attempted: body.dispatch.attempted === true,
      status: dispatchStatus,
    } : null,
  };
}

/**
 * One-shot, existing-job-only recovery. This deliberately performs one fetch
 * and never falls back to ordinary generation or retries an uncertain result.
 */
export async function resumeExistingCaiOpportunityJob(input: ResumeExistingCaiOpportunityJobRequest): Promise<Record<string, any>> {
  const identityIsValid = Boolean(input
    && UUID_PATTERN.test(input.resumeExistingJobId || "")
    && UUID_PATTERN.test(input.accountId || "")
    && UUID_PATTERN.test(input.runId || "")
    && UUID_PATTERN.test(input.recommendationId || "")
    && typeof input.recommendationSourceKey === "string"
    && input.recommendationSourceKey.startsWith("asset:")
    && ["local", "competitive"].includes(input.productArea));
  if (!identityIsValid) return { status: "error", code: "cai_recovery_identity_invalid" };

  const token = await accessToken();
  if (!token) return { status: "error", code: "cai_auth_required" };
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30000);
  try {
    const response = await fetch("/api/cai-opportunity-job", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({
        resumeExistingJobId: input.resumeExistingJobId,
        accountId: input.accountId,
        runId: input.runId,
        recommendationId: input.recommendationId,
        recommendationSourceKey: input.recommendationSourceKey,
        productArea: input.productArea,
        outputType: "CAI Output",
        opportunityCategory: "asset",
        retryRequested: false,
      }),
      signal: controller.signal,
    });
    const body = await safeJson(response);
    return sanitizedRecoveryResult(body, response.status);
  } catch {
    return { status: "uncertain", code: "cai_recovery_outcome_uncertain", httpStatus: null };
  } finally {
    clearTimeout(timeout);
  }
}

export async function getCaiOpportunityJobStatus(input: { accountId: string; jobId?: string; idempotencyKey?: string; runId?: string; recommendationSourceKey?: string }): Promise<Record<string, any>> {
  const token = await accessToken();
  if (!token) return { status: "status_unavailable", code: "cai_auth_required" };
  if (!input.accountId || (!input.jobId && !input.idempotencyKey)) return { status: "status_unavailable", code: "cai_status_identity_missing" };
  try {
    const params = new URLSearchParams({ account_id: input.accountId });
    if (input.jobId) params.set("job_id", input.jobId);
    if (input.idempotencyKey) params.set("idempotency_key", input.idempotencyKey);
    if (input.runId) params.set("run_id", input.runId);
    if (input.recommendationSourceKey) params.set("recommendation_source_key", input.recommendationSourceKey);
    const response = await fetch(`/api/cai-opportunity-job?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
    const body = await safeJson(response);
    if (body.status === "not_found" && response.ok) return { status: "not_found", code: "cai_job_not_found" };
    return body.status || body.code ? body : { status: response.ok ? "not_found" : "status_unavailable", code: response.ok ? "cai_job_not_found" : "cai_status_unavailable" };
  } catch {
    return { status: "status_unavailable", code: "cai_status_unavailable" };
  }
}

export async function getCaiAssetOpportunities(accountId: string, runId?: string | null): Promise<Record<string, any>> {
  const token = await accessToken();
  if (!token) return { status: "error", code: "cai_auth_required", assets: [] };
  if (!accountId) return { status: "error", code: "cai_account_required", assets: [] };
  try {
    const params = new URLSearchParams({ account_id: accountId });
    if (runId) params.set("run_id", runId);
    const response = await fetch(`/api/cai-asset-opportunities?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const body = await safeJson(response);
    if (!response.ok) return { status: "error", code: body.code || "cai_asset_opportunities_unavailable", assets: [] };
    return {
      status: "ok",
      runId: body.runId || null,
      assets: Array.isArray(body.assets) ? body.assets : [],
      reviewStatus: typeof body.reviewStatus === "string" ? body.reviewStatus : "no_reviewed_assets",
    };
  } catch {
    return { status: "error", code: "cai_asset_opportunities_unavailable", assets: [] };
  }
}
